import { useEffect, useState } from 'react';

import styled from 'styled-components';

import { LottieAnimation } from '@components/lottie-animation';
import { PlanTodoList, PlanTodoStatus } from '@components/plan';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

const STEP_KEYS = [
  'readingTriggers',
  'walkingBranches',
  'readingTexts',
  'checkingAttributes',
  'traversingJourney',
] as const;

export const STEP_COUNT = STEP_KEYS.length;

export const STEP_INTERVAL_MS = 2500;
export const FINISHING_STEP_MS = 1000;
// How long the all-ticked-off list is held before handing back to the panel.
export const COMPLETED_HOLD_MS = 500;

const LOADER_SIZE = 64;

const stepStatus = (index: number, activeStep: number): PlanTodoStatus => {
  if (index < activeStep) return 'completed';
  if (index === activeStep) return 'in_progress';
  return 'pending';
};

export type LoadingChecklistProps = {
  isFinishing?: boolean;
  onFinished?: () => void;
};

export const LoadingChecklist = ({ isFinishing = false, onFinished }: LoadingChecklistProps) => {
  const { t } = useScopedTranslate(locale);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    // Only the finishing pass may tick off the last step — while the review is
    // still running that step stays spinning.
    const lastStep = isFinishing ? STEP_COUNT : STEP_COUNT - 1;
    const intervalId = setInterval(
      () => setActiveStep((step) => Math.min(step + 1, lastStep)),
      isFinishing ? FINISHING_STEP_MS : STEP_INTERVAL_MS,
    );
    return () => clearInterval(intervalId);
  }, [isFinishing]);

  useEffect(() => {
    if (!isFinishing || activeStep < STEP_COUNT || !onFinished) return;
    const timeoutId = setTimeout(onFinished, COMPLETED_HOLD_MS);
    return () => clearTimeout(timeoutId);
  }, [isFinishing, activeStep, onFinished]);

  return (
    <Stack space={6} align="center">
      <Loader animation="journeys-loader" />
      <Stack space={2} align="center">
        <Text font="heading2" text={t('reviewing')} center />
        <Text font="regular14" color="textHelper" text={t('reviewingSubtitle')} center />
      </Stack>
      <PlanTodoList
        todos={STEP_KEYS.map((key, index) => {
          return { id: key, label: t(key), status: stepStatus(index, activeStep) };
        })}
      />
    </Stack>
  );
};

// The mark's colour is baked into the Lottie file; overriding the fill here is
// what lets it follow the theme.
const Loader = styled(LottieAnimation)({
  width: pxToRem(LOADER_SIZE),
  height: pxToRem(LOADER_SIZE),
  marginTop: pxToRem(32),
  color: theme.color.elementIndigo400,

  'svg rect': {
    fill: 'currentColor',
  },
});

export const locale = {
  en: {
    reviewing: 'Reviewing your journey',
    reviewingSubtitle: "I'm walking the whole thing end to end, checking fields, texts, and logic.",
    readingTriggers: 'Reading triggers',
    walkingBranches: 'Walking branches end to end',
    readingTexts: 'Reading traveller-facing texts',
    checkingAttributes: 'Checking journey attributes',
    traversingJourney: 'Tracing the whole journey',
  },
};
